<template>
  <div>
    <v-dialog
      v-model="dialog"
      width="1300"
      persistent
      scrollable
      v-if="localInvoice"
    >
      <v-card class="invoice-card">
        <!-- HEADER -->
        <v-toolbar flat dense color="primary" dark class="flex-grow-0">
          <v-icon left>mdi-note-text-outline</v-icon>
          <v-toolbar-title class="text-subtitle-1 font-weight-medium"
            >Invoice Card</v-toolbar-title
          >
          <v-chip
            small
            label
            color="white"
            text-color="primary"
            class="ml-3 font-weight-medium"
          >
            <v-icon x-small left>mdi-barcode</v-icon>
            {{ localInvoice.invoice_code }}
          </v-chip>
          <v-chip
            small
            label
            class="ml-2 font-weight-medium"
            :color="statusColor(localInvoice.invoice_status)"
          >
            {{ localInvoice.invoice_status }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <!-- INVOICE ITEMS TABLE -->
          <div>
            <div class="d-flex align-center mb-2">
              <v-icon small left color="grey darken-1"
                >mdi-format-list-bulleted</v-icon
              >
              <span class="text-subtitle-2 grey--text text--darken-2"
                >Items</span
              >
              <v-spacer></v-spacer>
              <template v-if="hasPermission('invoices.add_item')">
                <add-item-modal
                  :invoice_id="localInvoice.id"
                  @item-added="onItemAdded"
                />
                <add-service-modal
                  :invoice_id="localInvoice.id"
                  @service-added="onItemAdded"
                />
              </template>
            </div>
            <v-data-table
              :items="localInvoice.invoice_items"
              :headers="effectiveHeaders"
              dense
            >
              <template v-slot:item.item_action="{ item }">
                <div class="d-flex align-center">
                  <v-btn
                    v-if="can('invoices.edit_item_price')"
                    x-small
                    icon
                    color="primary"
                    @click="openPriceEdit(item)"
                    title="Edit Unit Price"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="can('invoices.delete_item')"
                    x-small
                    icon
                    color="red"
                    @click="del_item(item)"
                    :loading="deletingItemId === item.id"
                    title="Delete Item"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:item.item.item_name="{ item }">
                <div class="d-flex align-center flex-wrap">
                  <span v-if="item.type === 'service' && item.service">{{
                    item.service.service_name
                  }}</span>
                  <span v-else-if="item.item">{{ item.item.item_name }}</span>
                  <v-chip
                    v-if="isInserted(item)"
                    x-small
                    color="deep-purple lighten-4"
                    text-color="deep-purple darken-2"
                    class="ml-2 font-weight-medium"
                    label
                  >
                    <v-icon x-small left>mdi-plus-circle-outline</v-icon>
                    Inserted item
                  </v-chip>
                </div>
              </template>
              <template v-slot:item.serials="{ item }">
                <div
                  v-if="item.item_serials && item.item_serials.length"
                  class="d-flex flex-column py-1"
                >
                  <v-chip
                    v-for="sn in item.item_serials"
                    :key="sn.id"
                    x-small
                    outlined
                    color="green"
                    class="mb-1"
                  >
                    <v-icon left x-small>mdi-barcode</v-icon>
                    {{ sn.serial_number }}
                  </v-chip>
                </div>
                <span v-else class="grey--text caption">—</span>
              </template>
              <template v-slot:item.item.selling_price="{ item }">
                <span v-if="item.type === 'service' && item.service">{{
                  item.service.service_amount
                }}</span>
                <span v-else-if="item.item">{{ item.item.selling_price }}</span>
              </template>
              <template v-slot:item.item.warranty="{ item }">
                <span
                  v-if="
                    item.type === 'service' &&
                    item.service &&
                    item.service.warranty
                  "
                >
                  {{ item.service.warranty.warranty_count }} -
                  {{ item.service.warranty.warranty_duration }}
                </span>
                <span v-else-if="item.item && item.item.warranty">
                  {{ item.item.warranty.warranty_count }} -
                  {{ item.item.warranty.warranty_duration }}
                </span>
                <span v-else>No Warranty Stated</span>
              </template>
              <template
                v-slot:item.item.chart_of_account.account_title="{ item }"
              >
                <span
                  v-if="
                    item.type === 'service' &&
                    item.service &&
                    item.service.chart_of_account
                  "
                  >{{ item.service.chart_of_account.account_title }}</span
                >
                <span v-else-if="item.item && item.item.chart_of_account">{{
                  item.item.chart_of_account.account_title
                }}</span>
              </template>
              <template v-slot:item.warranty_remaining="{ item }">
                <span
                  v-if="!localInvoice.warranty_policy"
                  class="grey--text caption"
                  >N/A</span
                >
                <span v-else :class="getItemWarrantyClass(item)">
                  {{ getItemWarrantyText(item) }}
                </span>
              </template>
              <template v-slot:item.rma_action="{ item }">
                <v-btn
                  v-if="item.type !== 'service' && item.item"
                  x-small
                  color="orange"
                  dark
                  @click="openRmaForm(item)"
                >
                  <v-icon x-small class="mr-1">mdi-alert-circle-outline</v-icon>
                  File RMA
                </v-btn>
              </template>
            </v-data-table>
          </div>

          <!-- ITEM INSERTION HISTORY (hidden until expanded) -->
          <div v-if="insertedItems.length" class="mt-2">
            <v-btn
              text
              small
              color="deep-purple"
              class="px-2"
              @click="showInsertionHistory = !showInsertionHistory"
            >
              <v-icon small left>mdi-history</v-icon>
              Item Insertion History ({{ insertedItems.length }})
              <v-icon small right>{{
                showInsertionHistory ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
            <v-expand-transition>
              <v-card v-show="showInsertionHistory" outlined class="mt-2">
                <v-data-table
                  :items="insertedItems"
                  :headers="insertionHistoryHeaders"
                  dense
                  :items-per-page="5"
                >
                  <template v-slot:item.description="{ item }">
                    <span v-if="item.type === 'service' && item.service">{{
                      item.service.service_name
                    }}</span>
                    <span v-else-if="item.item">{{ item.item.item_name }}</span>
                  </template>
                  <template v-slot:item.remarks="{ item }">
                    <span v-if="item.remarks">{{ item.remarks }}</span>
                    <span v-else class="grey--text caption">—</span>
                  </template>
                </v-data-table>
              </v-card>
            </v-expand-transition>
          </div>

          <!-- DELETED ITEM HISTORY (hidden until expanded) -->
          <div v-if="deletedItems.length" class="mt-2">
            <v-btn
              text
              small
              color="red darken-1"
              class="px-2"
              @click="showDeletedHistory = !showDeletedHistory"
            >
              <v-icon small left>mdi-delete-clock-outline</v-icon>
              Deleted Item History ({{ deletedItems.length }})
              <v-icon small right>{{
                showDeletedHistory ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
            <v-expand-transition>
              <v-card v-show="showDeletedHistory" outlined class="mt-2">
                <v-data-table
                  :items="deletedItems"
                  :headers="deletedHistoryHeaders"
                  dense
                  :items-per-page="5"
                >
                  <template v-slot:item.description="{ item }">
                    <span v-if="item.type === 'service' && item.service">{{
                      item.service.service_name
                    }}</span>
                    <span v-else-if="item.item">{{ item.item.item_name }}</span>
                    <span v-else class="grey--text caption">—</span>
                  </template>
                  <template v-slot:item.amount="{ item }">
                    {{ formatCurrency(item.amount) }}
                  </template>
                  <template v-slot:item.deleted_by="{ item }">
                    <span v-if="item.deleted_by">{{ item.deleted_by }}</span>
                    <span v-else class="grey--text caption">—</span>
                  </template>
                  <template v-slot:item.deleted_at="{ item }">
                    <span v-if="item.deleted_at">{{
                      formatDateTime(item.deleted_at)
                    }}</span>
                    <span v-else class="grey--text caption">—</span>
                  </template>
                </v-data-table>
              </v-card>
            </v-expand-transition>
          </div>

          <!-- PRICE EDIT HISTORY (hidden until expanded) -->
          <div v-if="priceEdits.length" class="mt-2">
            <v-btn
              text
              small
              color="primary"
              class="px-2"
              @click="showPriceEditHistory = !showPriceEditHistory"
            >
              <v-icon small left>mdi-tag-text-outline</v-icon>
              Price Edit History ({{ priceEdits.length }})
              <v-icon small right>{{
                showPriceEditHistory ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
            <v-expand-transition>
              <v-card v-show="showPriceEditHistory" outlined class="mt-2">
                <v-data-table
                  :items="priceEdits"
                  :headers="priceEditHistoryHeaders"
                  dense
                  :items-per-page="5"
                >
                  <template v-slot:item.old_unit_price="{ item }">
                    {{ formatCurrency(item.old_unit_price) }}
                  </template>
                  <template v-slot:item.new_unit_price="{ item }">
                    {{ formatCurrency(item.new_unit_price) }}
                  </template>
                  <template v-slot:item.remarks="{ item }">
                    <span v-if="item.remarks">{{ item.remarks }}</span>
                    <span v-else class="grey--text caption">—</span>
                  </template>
                  <template v-slot:item.created_at="{ item }">
                    {{ formatDateTime(item.created_at) }}
                  </template>
                </v-data-table>
              </v-card>
            </v-expand-transition>
          </div>

          <!-- Edit Unit Price Dialog -->
          <v-dialog v-model="priceEditVisible" width="480" persistent>
            <v-card>
              <v-toolbar flat dense color="primary" dark class="flex-grow-0">
                <v-icon left>mdi-pencil</v-icon>
                <v-toolbar-title class="text-subtitle-1 font-weight-medium"
                  >Edit Unit Price</v-toolbar-title
                >
                <v-spacer></v-spacer>
                <v-btn icon @click="priceEditVisible = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-text class="pa-4">
                <div class="text-body-2 font-weight-medium mb-1">
                  {{ priceEditForm.item_name }}
                </div>
                <div class="caption grey--text mb-3">
                  Quantity: {{ priceEditForm.quantity }} · Current unit price:
                  {{ formatCurrency(priceEditForm.old_unit_price) }}
                </div>
                <v-text-field
                  v-model.number="priceEditForm.unit_price"
                  label="New Unit Price"
                  type="number"
                  min="0"
                  step="0.01"
                  prefix="₱"
                  outlined
                  dense
                  hide-details
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="priceEditForm.remarks"
                  label="Reason / Remarks (optional)"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
                <div class="mt-3 caption grey--text">
                  New line total:
                  <span class="font-weight-medium">{{
                    formatCurrency(newLineTotalPreview)
                  }}</span>
                </div>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions class="px-4 py-3">
                <v-spacer></v-spacer>
                <v-btn text @click="priceEditVisible = false">Cancel</v-btn>
                <v-btn
                  color="primary"
                  depressed
                  :loading="priceEditLoading"
                  @click="submitPriceEdit"
                  >Save</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- RMA Dialog -->
          <v-dialog v-model="rmaFormVisible" width="600" persistent>
            <v-card>
              <v-card-title>
                <v-icon small class="mr-2" color="orange"
                  >mdi-alert-circle-outline</v-icon
                >
                <span>File RMA</span>
                <v-spacer></v-spacer>
                <v-btn icon small @click="rmaFormVisible = false">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text>
                <div class="d-flex">
                  <v-text-field
                    v-model="rmaForm.item_name"
                    label="Item Name"
                    readonly
                    class="mr-3"
                  ></v-text-field>
                  <v-text-field
                    v-model="rmaForm.quantity"
                    label="Quantity"
                    type="number"
                    min="1"
                    class="mr-3"
                    style="max-width: 100px"
                  ></v-text-field>
                  <v-text-field
                    v-model="rmaForm.product_serial_number"
                    label="Serial Number"
                  ></v-text-field>
                </div>

                <v-text-field
                  v-model="rmaForm.technician"
                  label="Filed By"
                  readonly
                ></v-text-field>

                <!-- Lot selection (auto-loaded from invoice deductions) -->
                <div v-if="lotDeductions.length > 0" class="mb-3">
                  <p class="caption grey--text mb-1">
                    Stock batch(es) sold on this invoice &mdash; select the one
                    to link:
                  </p>
                  <v-data-table
                    :items="lotDeductions"
                    :headers="lotHeaders"
                    dense
                    hide-default-footer
                    :items-per-page="-1"
                  >
                    <template v-slot:item.lot_action="{ item: ded }">
                      <v-btn
                        x-small
                        :color="
                          rmaForm.item_stock_lot_id === ded.item_stock_lot_id
                            ? 'success'
                            : 'grey'
                        "
                        :dark="
                          rmaForm.item_stock_lot_id === ded.item_stock_lot_id
                        "
                        @click="toggleLot(ded)"
                      >
                        {{
                          rmaForm.item_stock_lot_id === ded.item_stock_lot_id
                            ? "Selected"
                            : "Select"
                        }}
                      </v-btn>
                    </template>
                    <template v-slot:item.batch_name="{ item: ded }">
                      {{
                        ded.lot && ded.lot.import_batch
                          ? ded.lot.import_batch.batch_description
                          : "N/A"
                      }}
                    </template>
                    <template v-slot:item.unit_cost="{ item: ded }">
                      &#8369;{{
                        ded.lot
                          ? Number(ded.lot.unit_cost).toLocaleString()
                          : "N/A"
                      }}
                    </template>
                    <template v-slot:item.supplier="{ item: ded }">
                      {{ ded.lot ? ded.lot.supplier || "N/A" : "N/A" }}
                    </template>
                  </v-data-table>
                </div>
                <p v-else-if="lotsLoading" class="caption grey--text">
                  Loading batch info...
                </p>

                <v-textarea
                  v-model="rmaForm.problem_description"
                  label="Problem Description"
                  placeholder="Describe the defect and any troubleshooting done."
                  rows="3"
                ></v-textarea>
              </v-card-text>

              <v-card-actions>
                <v-btn text @click="rmaFormVisible = false">Cancel</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  color="orange"
                  dark
                  @click="submitRma"
                  :loading="rmaLoading"
                >
                  Submit RMA
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- SUMMARY -->
          <v-card outlined class="mt-4">
            <div class="d-flex align-center px-4 py-2 grey lighten-4">
              <v-icon small left color="grey darken-1"
                >mdi-information-outline</v-icon
              >
              <span class="text-subtitle-2 grey--text text--darken-2"
                >Summary</span
              >
              <v-spacer></v-spacer>
              <edit-invoice-modal :invoice="localInvoice" />
            </div>
            <v-divider></v-divider>
            <v-card-text class="py-3">
              <v-row dense>
                <v-col
                  cols="6"
                  sm="3"
                  v-for="f in summaryFields"
                  :key="f.label"
                >
                  <div class="caption grey--text text--darken-1">
                    {{ f.label }}
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ f.value }}
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="caption grey--text text--darken-1">Balance</div>
                  <div
                    class="text-body-2 font-weight-bold"
                    :class="
                      Number(localInvoice.balance) > 0
                        ? 'orange--text text--darken-2'
                        : 'green--text text--darken-1'
                    "
                  >
                    {{ formatCurrency(localInvoice.balance) }}
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="caption grey--text text--darken-1">Warranty</div>
                  <v-chip
                    v-if="localInvoice.warranty_policy"
                    x-small
                    color="blue"
                    dark
                  >
                    {{ localInvoice.warranty_policy.name }}
                  </v-chip>
                  <span v-else class="text-body-2 grey--text">None</span>
                </v-col>
                <v-col cols="12" v-if="localInvoice.purpose">
                  <div class="caption grey--text text--darken-1">Purpose</div>
                  <div class="text-body-2">{{ localInvoice.purpose }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- SOP Information -->
          <v-card outlined class="mt-4">
            <div class="d-flex align-center px-4 py-2 grey lighten-4">
              <v-icon
                small
                left
                :color="localInvoice.with_sop ? 'info' : 'grey darken-1'"
                >mdi-cash-check</v-icon
              >
              <span class="text-subtitle-2 grey--text text--darken-2"
                >SOP (Special Offer Promotion)</span
              >
              <v-spacer></v-spacer>
              <v-chip
                :color="localInvoice.with_sop ? 'success' : 'grey'"
                x-small
                label
                :dark="localInvoice.with_sop"
              >
                {{ localInvoice.with_sop ? "Applied" : "Not Applied" }}
              </v-chip>
            </div>
            <template v-if="localInvoice.with_sop">
              <v-divider></v-divider>
              <v-card-text class="py-3">
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <div class="caption grey--text text--darken-1">
                      SOP Percentage
                    </div>
                    <div class="text-h6 blue--text text--darken-2">
                      {{ localInvoice.sop_percentage }}%
                    </div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="caption grey--text text--darken-1">
                      SOP Amount
                    </div>
                    <div class="text-h6 green--text text--darken-2">
                      {{ formatCurrency(localInvoice.sop_amount) }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="caption grey--text text--darken-1">
                      Cheque Amount
                    </div>
                    <div class="text-h6 orange--text text--darken-2">
                      {{ formatCurrency(localInvoice.cheque_amount) }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </template>
          </v-card>

          <!-- PAYMENTS -->
          <div class="d-flex align-center mt-4 mb-2">
            <v-icon small left color="grey darken-1">mdi-cash-multiple</v-icon>
            <span class="text-subtitle-2 grey--text text--darken-2"
              >Payments</span
            >
          </div>
          <div>
            <v-data-table
              :items="localInvoice.payments"
              :headers="payment_headers"
              dense
            >
              <template v-slot:item.chart_of_account.account_title="{ item }">
                <span v-if="item.chart_of_account">{{
                  item.chart_of_account.account_title
                }}</span>
              </template>
              <template v-slot:item.user.full_name="{ item }">
                <span v-if="item.user">{{ item.user.full_name }}</span>
              </template>
              <template v-slot:item.payment_action="{ item }">
                <div class="d-flex align-center">
                  <v-btn
                    x-small
                    icon
                    color="primary"
                    @click="print_acknowledgement(item)"
                    title="Print Acknowledgement Receipt"
                  >
                    <v-icon small>mdi-printer</v-icon>
                  </v-btn>
                  <template v-if="can('payables.edit_payment')">
                    <edit-payment-modal
                      :payment="item"
                      :invoice="localInvoice"
                    />
                    <v-btn
                      x-small
                      icon
                      color="red"
                      @click="del(item)"
                      :loading="loading"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-4">
          <span class="caption grey--text">payablesModal.vue</span>
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="$emit('close')"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card></v-dialog
    >
  </div>
</template>

<script>
import addItemModal from "./addItemModal.vue";
import addServiceModal from "./addServiceModal.vue";
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import EditInvoiceModal from "../Invoice/editInvoiceModal.vue";
import EditPaymentModal from "./editPaymentModal.vue";
export default {
  created() {
    // console.log(this.invoice);
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    effectiveHeaders() {
      if (
        this.can("invoices.delete_item") ||
        this.can("invoices.edit_item_price")
      ) {
        return [
          { text: "", value: "item_action", width: 90, sortable: false },
          ...this.headers,
        ];
      }
      return this.headers;
    },
    priceEdits() {
      return (
        (this.localInvoice && this.localInvoice.invoice_item_price_edits) || []
      );
    },
    newLineTotalPreview() {
      const unit = Number(this.priceEditForm.unit_price) || 0;
      const qty = Number(this.priceEditForm.quantity) || 1;
      return unit * qty;
    },
    insertedItems() {
      const items =
        (this.localInvoice && this.localInvoice.invoice_items) || [];
      return items.filter((i) => this.isInserted(i));
    },
    deletedItems() {
      return (
        (this.localInvoice && this.localInvoice.deleted_invoice_items) || []
      );
    },
    summaryFields() {
      const inv = this.localInvoice || {};
      return [
        { label: "Amount", value: this.formatCurrency(inv.amount) },
        { label: "Discount", value: this.formatCurrency(inv.discount) },
        { label: "VAT", value: this.formatCurrency(inv.vat) },
        { label: "Total Amount", value: this.formatCurrency(inv.total_amount) },
        { label: "Date", value: inv.created_at },
        { label: "Inserted by", value: inv.inserted_by },
      ];
    },
  },
  watch: {
    invoice: {
      immediate: true,
      handler(val) {
        this.localInvoice = val ? JSON.parse(JSON.stringify(val)) : null;
      },
    },
  },
  props: ["invoice"],
  methods: {
    ...mapActions({
      delete_invoice_item: "invoice/delete_invoice_item",
      update_invoice_item_price: "invoice/update_invoice_item_price",
      add_returned_item: "item/add_returned_item",
      get_invoice_lot_deductions: "item/get_invoice_lot_deductions",
      give_replacement: "item/give_replacement",
    }),
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    formatCurrency(amount) {
      if (!amount) return "₱0.00";
      return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
      }).format(amount);
    },
    formatDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    statusColor(status) {
      if (status === "Paid") return "green lighten-4";
      if (status === "With Balance" || status === "Waiting for Payment")
        return "orange lighten-4";
      return "grey lighten-2";
    },
    // An "inserted" item is one added to the invoice after creation via the
    // Add Item / Add Service modals (which stamp added_by + date_added). Items
    // created together with the invoice leave these null.
    isInserted(item) {
      return !!(item && (item.date_added || item.added_by));
    },
    // === Warranty Remaining Helpers ===
    addWarrantyToDate(date, value, unit) {
      const d = new Date(date);
      if (unit === "months") d.setMonth(d.getMonth() + value);
      else if (unit === "days") d.setDate(d.getDate() + value);
      else if (unit === "years") d.setFullYear(d.getFullYear() + value);
      return d;
    },
    getItemWarrantyInfo(invoiceItem) {
      const wp = this.localInvoice.warranty_policy;
      if (!wp) return null;

      const invoiceDate = new Date(this.localInvoice.invoice_date);
      if (isNaN(invoiceDate.getTime())) return null;

      let value, unit, label;

      if (invoiceItem.type === "service") {
        value = wp.service_warranty_value;
        unit = wp.service_warranty_unit;
        label = "Service";
      } else if (invoiceItem.item) {
        const classification =
          invoiceItem.item.part_classification ||
          (invoiceItem.item.item_type &&
            invoiceItem.item.item_type.part_classification);
        if (classification === "major") {
          value = wp.major_parts_warranty_value;
          unit = wp.major_parts_warranty_unit;
          label = "Major";
        } else if (classification === "minor") {
          value = wp.minor_parts_warranty_value;
          unit = wp.minor_parts_warranty_unit;
          label = "Minor";
        } else {
          // Unclassified — use the shorter (conservative)
          const majorExpiry = this.addWarrantyToDate(
            invoiceDate,
            wp.major_parts_warranty_value,
            wp.major_parts_warranty_unit
          );
          const minorExpiry = this.addWarrantyToDate(
            invoiceDate,
            wp.minor_parts_warranty_value,
            wp.minor_parts_warranty_unit
          );
          if (majorExpiry <= minorExpiry) {
            value = wp.major_parts_warranty_value;
            unit = wp.major_parts_warranty_unit;
          } else {
            value = wp.minor_parts_warranty_value;
            unit = wp.minor_parts_warranty_unit;
          }
          label = "Unclassified";
        }
      } else {
        return null;
      }

      if (!value || !unit) return { days: null, label };

      const expiry = this.addWarrantyToDate(invoiceDate, value, unit);
      const now = new Date();
      const days = Math.ceil(
        (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      return { days, label };
    },
    getItemWarrantyText(invoiceItem) {
      const info = this.getItemWarrantyInfo(invoiceItem);
      if (!info || info.days === null) return "N/A";
      if (info.days <= 0) return "Expired (" + info.label + ")";
      return (
        info.days +
        " day" +
        (info.days !== 1 ? "s" : "") +
        " (" +
        info.label +
        ")"
      );
    },
    getItemWarrantyClass(invoiceItem) {
      const info = this.getItemWarrantyInfo(invoiceItem);
      if (!info || info.days === null) return "grey--text caption";
      if (info.days <= 0) return "red--text caption font-weight-bold";
      if (info.days <= 30) return "orange--text caption font-weight-bold";
      return "green--text caption font-weight-bold";
    },
    // === RMA Methods ===
    async openRmaForm(invoiceItem) {
      // Check warranty status before opening RMA form
      if (invoiceItem.item_id && this.localInvoice.warranty_policy) {
        try {
          const { data } = await axios.get(
            `/invoice/${this.localInvoice.id}/warranty_check`,
            { params: { item_id: invoiceItem.item_id } }
          );
          if (data.is_covered === false) {
            this.$alert({
              title: "Cannot File RMA — Warranty Expired",
              message:
                `The <b>${
                  data.warranty_type || "warranty"
                }</b> for this item has expired.<br><br>` +
                `<b>Policy:</b> ${data.policy_name}<br>` +
                `<b>Invoice Date:</b> ${data.invoice_date}<br>` +
                `<b>Warranty Expiry:</b> ${data.warranty_expiry}<br>` +
                `<b>Expired:</b> ${Math.abs(
                  data.days_remaining
                )} day(s) ago<br><br>` +
                `This item's warranty coverage (<b>${data.warranty_type}</b>) has expired. RMA cannot be filed.`,
              color: "error",
              icon: "mdi-shield-alert-outline",
            });
            return;
          }
        } catch (err) {
          console.error("Warranty check failed:", err);
          // If warranty check fails, allow proceeding
        }
      }

      this.rmaForm.item_id = invoiceItem.item_id;
      this.rmaForm.item_name = invoiceItem.item
        ? invoiceItem.item.item_name
        : "";
      this.rmaForm.quantity = invoiceItem.quantity;
      this.rmaForm.product_serial_number = "";
      this.rmaForm.problem_description = "";
      this.rmaForm.technician = this.user.full_name;
      this.rmaForm.invoice_id = this.localInvoice.id;
      this.rmaForm.invoice_item_id = invoiceItem.id;
      this.rmaForm.item_stock_lot_id = null;
      this.rmaFormVisible = true;

      // Load lot deductions for this invoice to auto-link the lot
      this.lotsLoading = true;
      try {
        const deductions = await this.get_invoice_lot_deductions(
          this.localInvoice.id
        );
        // Filter to only deductions for this specific item
        this.lotDeductions = deductions.filter(
          (d) => d.item_id === invoiceItem.item_id
        );
        // Auto-select if there's exactly one lot
        if (this.lotDeductions.length === 1) {
          this.rmaForm.item_stock_lot_id =
            this.lotDeductions[0].item_stock_lot_id;
        }
      } catch (err) {
        console.error("Failed to load lot deductions:", err);
        this.lotDeductions = [];
      }
      this.lotsLoading = false;
    },
    toggleLot(deduction) {
      if (this.rmaForm.item_stock_lot_id === deduction.item_stock_lot_id) {
        this.rmaForm.item_stock_lot_id = null;
      } else {
        this.rmaForm.item_stock_lot_id = deduction.item_stock_lot_id;
      }
    },
    async submitRma() {
      if (!this.rmaForm.item_id) {
        this.$alert({
          title: "Validation",
          message: "No item selected.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.rmaForm.problem_description) {
        this.$alert({
          title: "Validation",
          message: "Please describe the problem.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      let confirmed = await this.$confirm({
        title: "File RMA",
        message: "File RMA for <b>" + this.rmaForm.item_name + "</b>?",
        color: "orange",
        icon: "mdi-alert-circle-outline",
      });
      if (!confirmed) return;

      this.rmaLoading = true;
      try {
        const returnedItem = await this.add_returned_item(this.rmaForm);
        this.rmaFormVisible = false;

        // Ask if user wants to proceed with replacing the item to the customer
        const wantsReplace = await this.$confirm({
          title: "Give Replacement?",
          message:
            "RMA filed successfully.<br><br>Would you like to proceed with <b>replacing the item</b> to the customer now? This will deduct 1 unit from shelf stock (FIFO).",
          confirmText: "Yes, Replace Now",
          cancelText: "No, Later",
          color: "success",
          icon: "mdi-swap-horizontal",
        });

        if (wantsReplace) {
          try {
            await this.give_replacement(returnedItem.id);
            this.$alert({
              title: "Success",
              message: "Replacement given to customer successfully.",
              color: "success",
              icon: "mdi-check-circle",
            });
          } catch (err) {
            this.$alert({
              title: "Error",
              message:
                "Replacement failed: " +
                (err.response ? err.response.data.error : err.message),
              color: "error",
              icon: "mdi-alert-circle",
            });
          }
        }
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.rmaLoading = false;
    },
    print_acknowledgement(payment) {
      const {
        generateAcknowledgementReceipt,
      } = require("@/utils/dotMatrixPrinter");
      // Original full-width layout; two copies divide the A4 into top and
      // bottom halves, with a reduced font so both fit on a single sheet.
      const printer = generateAcknowledgementReceipt(
        this.localInvoice,
        payment
      );
      printer.print({ copies: 2 });
    },
    // === Existing Methods ===
    onItemAdded(updatedInvoice) {
      Object.assign(this.localInvoice, updatedInvoice);
    },
    openPriceEdit(item) {
      const qty = Number(item.quantity) || 1;
      const oldUnit = qty ? Number(item.amount) / qty : Number(item.amount);
      let name = "Line item";
      if (item.type === "service" && item.service)
        name = item.service.service_name;
      else if (item.item) name = item.item.item_name;
      this.priceEditForm = {
        invoice_item_id: item.id,
        item_name: name,
        quantity: qty,
        old_unit_price: Math.round(oldUnit * 100) / 100,
        unit_price: Math.round(oldUnit * 100) / 100,
        remarks: "",
      };
      this.priceEditVisible = true;
    },
    async submitPriceEdit() {
      const newUnit = Number(this.priceEditForm.unit_price);
      if (isNaN(newUnit) || newUnit < 0) {
        this.$alert({
          title: "Validation",
          message: "Please enter a valid unit price.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (
        Math.round(newUnit * 100) ===
        Math.round(this.priceEditForm.old_unit_price * 100)
      ) {
        this.$alert({
          title: "Validation",
          message: "The unit price is unchanged.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      const confirmed = await this.$confirm({
        title: "Edit Unit Price",
        message:
          "Change the unit price of <b>" +
          this.priceEditForm.item_name +
          "</b> from " +
          this.formatCurrency(this.priceEditForm.old_unit_price) +
          " to " +
          this.formatCurrency(newUnit) +
          "?<br><br>This adjusts the invoice total and posts a balanced journal adjustment.",
        color: "primary",
        icon: "mdi-pencil",
      });
      if (!confirmed) return;

      this.priceEditLoading = true;
      try {
        const updatedInvoice = await this.update_invoice_item_price({
          invoice_item_id: this.priceEditForm.invoice_item_id,
          payload: {
            unit_price: newUnit,
            remarks: this.priceEditForm.remarks || null,
          },
        });
        if (updatedInvoice && updatedInvoice.id) {
          Object.assign(this.localInvoice, updatedInvoice);
        }
        this.priceEditVisible = false;
        this.$alert({
          title: "Success",
          message: "Unit price updated.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            (err.response && err.response.data && err.response.data.error) ||
            String(err),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.priceEditLoading = false;
    },
    async del_item(item) {
      let x = await this.$confirm({
        title: "Delete Item",
        message: "Are you sure you want to delete this item?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.deletingItemId = item.id;
        try {
          const updatedInvoice = await this.delete_invoice_item(item.id);
          if (updatedInvoice && updatedInvoice.id) {
            Object.assign(this.localInvoice, updatedInvoice);
          } else {
            // Fallback: remove item locally
            const idx = this.localInvoice.invoice_items.findIndex(
              (i) => i.id === item.id
            );
            if (idx !== -1) this.localInvoice.invoice_items.splice(idx, 1);
          }
          this.$alert({
            title: "Success",
            message: "Item removed from invoice.",
            color: "success",
            icon: "mdi-check-circle",
          });
        } catch (err) {
          this.$alert({
            title: "Error",
            message: String(err),
            color: "error",
            icon: "mdi-alert-circle",
          });
        }
        this.deletingItemId = null;
      }
    },
    async del(item) {
      let x = await this.$confirm({
        title: "Delete Payment",
        message: "Are you sure you want to delete this payment?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.loading = true;
        this.delete_payment(item.id).then(() => {
          this.$alert({
            title: "Success",
            message: "Successfully deleted a payment!",
            color: "success",
            icon: "mdi-check-circle",
          });
          this.loading = false;
        });
      }
    },
    async delete_payment(payment_id) {
      await axios
        .delete("/payment/" + payment_id + "/" + this.localInvoice.id)
        .then((response) => {
          const updatedInvoice = response.data;
          // Update all localInvoice fields so UI reflects changes
          Object.assign(this.localInvoice, updatedInvoice);
        });
    },
  },

  data() {
    return {
      localInvoice: null,
      deletingItemId: null,
      loading: false,
      dialog: true,
      showInsertionHistory: false,
      insertionHistoryHeaders: [
        { text: "ID", value: "id", width: 70 },
        { text: "Description", value: "description" },
        { text: "Remarks", value: "remarks" },
        { text: "Added By", value: "added_by" },
        { text: "Date Added", value: "date_added" },
      ],
      showPriceEditHistory: false,
      priceEditHistoryHeaders: [
        { text: "ID", value: "id", width: 70 },
        { text: "Old Unit Price", value: "old_unit_price" },
        { text: "New Unit Price", value: "new_unit_price" },
        { text: "Qty", value: "quantity", width: 70 },
        { text: "Remarks", value: "remarks" },
        { text: "Edited By", value: "edited_by" },
        { text: "Date", value: "created_at" },
      ],
      priceEditVisible: false,
      priceEditLoading: false,
      priceEditForm: {
        invoice_item_id: null,
        item_name: "",
        quantity: 1,
        old_unit_price: 0,
        unit_price: 0,
        remarks: "",
      },
      showDeletedHistory: false,
      deletedHistoryHeaders: [
        { text: "ID", value: "id", width: 70 },
        { text: "Description", value: "description" },
        { text: "Qty", value: "quantity", width: 70 },
        { text: "Amount", value: "amount" },
        { text: "Deleted By", value: "deleted_by" },
        { text: "Date Deleted", value: "deleted_at" },
      ],
      // RMA state
      rmaFormVisible: false,
      rmaLoading: false,
      lotsLoading: false,
      lotDeductions: [],
      rmaForm: {
        item_id: null,
        item_name: "",
        quantity: 1,
        product_serial_number: "",
        problem_description: "",
        technician: "",
        invoice_id: null,
        invoice_item_id: null,
        item_stock_lot_id: null,
      },
      lotHeaders: [
        { text: "", value: "lot_action", width: 80, sortable: false },
        { text: "Batch ID", value: "item_stock_lot_id", width: 80 },
        { text: "Batch Name", value: "batch_name" },
        { text: "Supplier", value: "supplier" },
        { text: "Unit Cost", value: "unit_cost", width: 120 },
        { text: "Qty Deducted", value: "quantity", width: 110 },
      ],
      // Existing data
      payment_headers: [
        {
          text: "Actions",
          value: "payment_action",
          width: 80,
          sortable: false,
        },
        {
          text: "ID",
          value: "id",
        },
        {
          text: "Amount Paid",
          value: "amount_paid",
        },
        { text: "Payment Method", value: "chart_of_account.account_title" },
        { text: "Payment Date", value: "payment_date" },
        { text: "Tax Code", value: "tax_code" },
        { text: "OR Number", value: "or_number" },
        { text: "Posted by", value: "user.full_name" },
      ],
      headers: [
        // { text: "Action", value: "actions" },
        {
          text: "ID",
          value: "id",
        },
        {
          text: "Description",
          value: "item.item_name",
        },
        {
          text: "Serial No.",
          value: "serials",
          sortable: false,
        },
        {
          text: "Batch Import",
          value: "item.import_batch.batch_description",
        },
        {
          text: "Amount",
          value: "item.selling_price",
        },
        { text: "Quantity", value: "quantity" },
        {
          text: "Unit",
          value: "item.unit",
        },
        {
          text: "Total Amount",
          value: "amount",
        },

        {
          text: "Warranty Remaining",
          value: "warranty_remaining",
          sortable: false,
        },
        { text: "Actions", value: "rma_action", width: 120, sortable: false },
      ],
    };
  },
  components: {
    EditInvoiceModal,
    EditPaymentModal,
    AddItemModal: addItemModal,
    AddServiceModal: addServiceModal,
  },
};
</script>
<style lang="scss" scoped></style>
